import React, { useState } from 'react';
import { redirectToSubdomain, getAppPublicPathUrl } from '../utils/tenant';
import { clearTenantData, clearAdminData } from './auth/SubdomainAuthGuard.tsx';
import { User, Lock, LogIn, Eye, EyeOff } from 'lucide-react';
import axiosInstance from '../api/axios';
import { AUTH_PATHS, DEFAULT_PATHS } from '../routing/paths.ts';

function getUserIdString(user) {
  if (!user || typeof user !== 'object') return '';
  const id = user.id ?? user.user_id ?? user.userId;
  if (id === null || id === undefined || id === '') return '';
  return String(id);
}

function getSignInErrorMessage(err) {
  const apiError = err.response?.data?.error ?? err.response?.data?.message;
  const status = err.response?.status;
  if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
    return 'Network error. Check your connection and try again.';
  }
  if (status === 401) return apiError || 'Invalid username or password';
  return apiError || 'Sign in failed';
}

function SignIn({ onSignIn }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    if (!trimmedUsername || !trimmedPassword) {
      setError('Please enter username and password');
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post('/api/signin', {
        username: trimmedUsername,
        password: trimmedPassword,
        use_handoff: true,
      });
      // Log sign-in response in development so it remains visible in DevTools
      const {
        user: userData,
        organization_id,
        organization_slug,
        handoff_token: handoffTokenFromApi,
      } = response.data;

      localStorage.setItem('auth_user', JSON.stringify(userData));
      localStorage.setItem('username', userData.username);
      const userIdStr = getUserIdString(userData);
      if (userIdStr) {
        localStorage.setItem('userId', userIdStr);
      }
      if (organization_id) {
        localStorage.setItem('organization_id', organization_id);
      }
      const isAdmin = userData?.is_platform_admin;

      // Admin: clear tenant data; Tenant: clear admin data
      if (isAdmin) {
        clearTenantData();
      } else {
        clearAdminData();
      }

      // Store role/org for subdomain redirects
      localStorage.setItem('is_platform_admin', isAdmin ? 'true' : 'false');
      if (!isAdmin) {
        localStorage.setItem('organization_slug', organization_slug);
      }

      // Admin: redirect to admin subdomain with short-lived handoff token only.
      if (isAdmin) {
        redirectToSubdomain('admin', DEFAULT_PATHS.ADMIN_LANDING, {
          handoff_token: handoffTokenFromApi || '',
          username: userData.username,
          organization_id: organization_id || '',
          is_platform_admin: 'true',
          ...(userIdStr ? { user_id: userIdStr } : {}),
        });
        setTimeout(() => {
          onSignIn(userData);
         }, 1000);
        return;
      }

      // Tenant: redirect to tenant subdomain with short-lived handoff token only.
      redirectToSubdomain(organization_slug, DEFAULT_PATHS.TENANT_LANDING, {
        handoff_token: handoffTokenFromApi || '',
        username: userData.username,
        organization_slug: organization_slug,
        organization_id: organization_id || '',
        is_platform_admin: 'false',
        ...(userIdStr ? { user_id: userIdStr } : {}),
      });
     setTimeout(() => {
      onSignIn(userData);
     }, 1000);
    } catch (err) {
      setError(getSignInErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  
  const handleSignUpClick = () => {
    window.location.href = getAppPublicPathUrl(AUTH_PATHS.SIGNUP);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-200 rounded-full opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full opacity-10 animate-spin" style={{ animationDuration: '20s' }}></div>
      </div>
      
      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10 animate-fadeIn">
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute inset-0 bg-blue-500 blur-xl opacity-30 animate-pulse"></div>
            <h1 className="relative text-4xl font-bold text-blue-800" style={{
              fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif",
              letterSpacing: "0.2em",
              fontWeight: "700",
              textTransform: "uppercase"
            }}>
              SKEPTICS
            </h1>
          </div>
        </div>
        <p className="mt-2 text-center text-lg text-gray-600 animate-slideUp" style={{ animationDelay: '0.1s' }}>
          Welcome back
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-white/80 backdrop-blur-lg py-8 px-4 shadow-2xl rounded-2xl sm:px-10 border border-white/50 animate-slideUp" style={{ animationDelay: '0.15s' }}>
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg animate-shake">
                {error}
              </div>
            )}

            <div className="animate-fadeIn" style={{ animationDelay: '0.2s' }}>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-blue-400"
                  placeholder="Enter your username"
                />
              </div>
            </div>

            <div className="animate-fadeIn" style={{ animationDelay: '0.25s' }}>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-blue-400"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="animate-fadeIn" style={{ animationDelay: '0.3s' }}>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-sm font-medium text-white bg-gradient-to-r from-blue-800 to-indigo-700 hover:from-blue-700 hover:to-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-105 active:scale-95"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <LogIn className="h-5 w-5 mr-2" />
                    Sign In
                  </div>
                )}
              </button>
            </div>

            <div className="text-center animate-fadeIn" style={{ animationDelay: '0.35s' }}>
              <button
                type="button"
                onClick={handleSignUpClick}
                className="text-sm text-gray-600 hover:text-blue-800 underline transition-all duration-200 transform hover:scale-105"
              >
                Don't have an account? Sign up
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
          opacity: 0;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out forwards;
          opacity: 0;
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
}

export default SignIn;